import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { PreferencesConfigurationssModel, PROFILE_SOURCES, UPEnums, UPGeneralFields, UPServiceFields } from '../../model/user-profile.model';
import { Grid, GridConfig } from '@inavista/components/grid';
import { LOCAL_ROWS_URL, LocalRowsGridApi, provideLocalGridRows } from 'projects/service/src/app/shared/utils/local-rows-grid-api';
import { UserProfileService } from '../../services/user-profile.service';
import { CommonService } from '../../../../shared/services/common.service';
import { Applications, ISignal, RolePermissionsModel } from '../../../../shared/models/app.model';
import { Subject, takeUntil } from 'rxjs';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

@Component({
    selector: 'app-up-preferences',
    templateUrl: './up-preferences.component.html',
    styleUrls: ['./up-preferences.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [...provideLocalGridRows()],
    standalone: false
})
export class UpPreferencesComponent implements OnInit, OnChanges, OnDestroy {
  isFormSubmitted: any;
  durationsForm: FormGroup;

  durationsData!: { id: number; duration: number; unit: string; description: string; }[];
  userId: string = '';
  hasUnsavedChanges: boolean = false;
  permissionsCategory: string = '';
  isCreatingNewUser: boolean = false;
  onExpirationChange(e: any) {
    e?.isInteracted ? this.onPreferencesModified.emit(true) : false;
    this.hasUnsavedChanges = true;
  }


  @Input('configs') configs!: PreferencesConfigurationssModel;

  @Input() actions: Subject<ISignal> = new Subject();

  @Input('userProfile') userProfile: any;
  @Input('isSectionEnabled') isSectionEnabled: boolean = false;
  @Input('source') source: PROFILE_SOURCES = UPEnums.SourceUser;
  @Output() onPreferencesModified = new EventEmitter<boolean>();

  private destroy$: Subject<any> = new Subject();
  PreferencesFields: typeof PreferencesFields = PreferencesFields;
  UPEnums: typeof UPEnums = UPEnums;
  PERMISSIONS: RolePermissionsModel[] = [];
  showGrid: boolean = false;

  constructor(private usersService: UserProfileService, private commonService: CommonService, private fb: FormBuilder) {
    this.durationsForm = this.fb.group({});
  }
  ngOnChanges(changes: SimpleChanges): void {
    const isSectionEnabled = changes?.isSectionEnabled?.currentValue;
    if (changes?.isSectionEnabled?.previousValue && isSectionEnabled) {
      if (isSectionEnabled) {
        this.durationsForm.controls[PreferencesFields.notificationExpiry].enable();
        this.durationsForm.controls[PreferencesFields.notificationReadExpiry].enable();
      } else {
        this.durationsForm.controls[PreferencesFields.notificationExpiry].disable();
        this.durationsForm.controls[PreferencesFields.notificationReadExpiry].disable();
      }
    }
  }

  public data: any[] = [];

  // The notification preferences tree (Sales or Service, configs.gridId), on @inavista/components: the rows are this
  // section's own (getPreferences), changed here and saved with the profile, through LocalRowsGridApi.
  @ViewChild('preferencesTree') tree?: Grid;
  @ViewChild('emailCell', { static: true }) emailCell!: TemplateRef<unknown>;
  @ViewChild('notificationCell', { static: true }) notificationCell!: TemplateRef<unknown>;
  @ViewChild('smsCell', { static: true }) smsCell!: TemplateRef<unknown>;
  private readonly treeRows = inject(LocalRowsGridApi);
  private readonly treeUrl = `${LOCAL_ROWS_URL}user-profile/preferences`;
  treeConfig: GridConfig | null = null;

  ngOnInit(): void {
    this.userId =  this.source == UPEnums.SourceMyProfile ? '' :  this.userProfile?.[UPGeneralFields.userId];
    this.isCreatingNewUser = (!this.userId && this.source == UPEnums.SourceUser);
    if(this.source == UPEnums.SourceMyProfile && this.configs.appName == Applications.RimsSales) {
      this.permissionsCategory = UPEnums.MPSalesPermission;
    }
    else if(this.source == UPEnums.SourceMyProfile && this.configs.appName == Applications.RimsService) {
      this.permissionsCategory = UPEnums.MPServicePermission;
    }
    else if(this.source == UPEnums.SourceUser && this.configs.appName == Applications.RimsSales) {
      this.permissionsCategory = UPEnums.UPSalesPermission;
    }
    else if(this.source == UPEnums.SourceUser && this.configs.appName == Applications.RimsService) {
      this.permissionsCategory = UPEnums.UPServicePermission;
    }
    this.durationsForm = this.fb.group({});
    this.getDurations();
    this.loadPermissions();
    this.handleActions();
    this.initForm();
  }


  initForm() {
    this.durationsForm.addControl(PreferencesFields.notificationExpiry, new FormControl('5'));
    this.durationsForm.addControl(PreferencesFields.notificationReadExpiry, new FormControl('5'));
  }
  // Tooltip of a duration dropdown, from the form value (the dropdown's own text is set mid change detection: NG0100)
  durationTitle(field: string): string {
    const value = this.durationsForm.get(field)?.value;
    return (this.durationsData as any[])?.find(d => d.value == value)?.text || 'Select a value';
  }
  getDurations() {
    this.usersService.getNotificationDurations().subscribe({
      next: (res: any) => {
        this.durationsData = res?.result;
      },
      error: (err: any) => {
        this.commonService.showNotification('error', err?.error?.message);
      },
    })
  }
  loadPermissions(
    category: string = this.permissionsCategory,
    subCategory: string = this.userProfile?.[UPServiceFields.role],
    id: string = this.userId,
    module: string = ''
  ) {
    this.showGrid = false;
    this.usersService.apiCallStarted("loadPermissions_Preferences");
    let api = this.isCreatingNewUser ?
    this.usersService.getCreateProfilePermissions(category, subCategory) :
    this.usersService.getEditProfilePermissions(category, subCategory, id);
    api.subscribe({
      next: (res: any) => {
        this.PERMISSIONS = res?.result ?? [];
        this.PERMISSIONS.map((permission: RolePermissionsModel) => {
          permission.ruleAction = permission.ruleAction
          .replace('MP_Service_', '')
          .replace('MP_Sales_', '')
          .replace('UP_Service_', '')
          .replace('UP_Sales_', '');
        });
        this.showGrid = false;
        this.updatePagePermissions().then(() => {
          this.showGrid = true;
        });
        this.usersService.apiCallCompleted("loadPermissions_Preferences");
      },
      error: (err: any) => {
        this.PERMISSIONS = [];
        this.usersService.apiCallCompleted("loadPermissions_Preferences");
      }
    });
  }
  updatePagePermissions(): Promise<boolean> {
    return new Promise((resolve) => {
      [
        { control: PreferencesFields.notificationReadExpiry, rule: 'readNotificationExpiration' },
        { control: PreferencesFields.notificationExpiry, rule: 'notificationExpiration' }
      ].forEach((e) => { this.updateUIElement(e.control, e.rule) });
        resolve(true);
    });
  }
  updateUIElement(control: string, rule: string) {
    const RULE = this.getPermission(rule);
    if(!RULE) {
      // console.log("This permission is missing in DB: ", rule);
    } else {
      (!RULE?.enable || !!RULE?.readOnly) ?
        this.durationsForm.controls[control].disable() :
        this.durationsForm.controls[control].enable();
      (!!RULE?.required && !!RULE?.visible) ?
        this.durationsForm.controls[control].addValidators(Validators.required) :
        this.durationsForm.controls[control].removeValidators(Validators.required);
    }
  }
  getPermission(ruleAction: string): RolePermissionsModel {
    return this.PERMISSIONS?.find((e: RolePermissionsModel) => e.ruleAction === ruleAction)!;
  }
  getPreferencesData() {
    this.showGrid = false;
    if(this.configs?.roleId) {
      this.usersService.apiCallStarted("getPreferencesData");
      const userId = this.userProfile?.[UPGeneralFields.userId];
      this.usersService.getPreferences(this.configs?.hasRoleIdChanged == true ? '' : userId, this.configs.appName, this.configs.roleId) // missing role parameter
        .subscribe({
          next: (res: any) => {
            this.data = res?.result ?? [];
            (res.result && res?.result[0]) && this.durationsForm.patchValue(res?.result[0]);
            // this.data?.map(d => d['isSMSDisabled'] = true)
            this.data?.forEach(el => {
              var parentData: any = el;
              parentData.isExpanded = true;
              [PreferencesFields.isEmailChecked, PreferencesFields.isNotificationChecked, PreferencesFields.isSMSChecked]
                .forEach((field => {
                  // parentData.subTasks[0] && (parentData.subTasks[0].isSMSDisabled = true);
                  // parentData.subTasks[0] && (parentData.subTasks[0].isEmailDisabled = true);
                  // parentData.subTasks[0] && (parentData.subTasks[0].isNotificationDisabled = true);
                  const disabledField = field === PreferencesFields.isEmailChecked ? 'isEmailDisabled'
                    : field === PreferencesFields.isNotificationChecked ? 'isNotificationDisabled'
                    : 'isSMSDisabled';
                  let subTasks: any[] = parentData?.subTasks;
                  let nonDisabledSubTasks = subTasks?.filter((e: any) => !e?.[disabledField]);
                  let areAllChildrenSelected = (nonDisabledSubTasks?.length > 0) && nonDisabledSubTasks?.every((e: any) => e?.[field]);
                  parentData[field] = areAllChildrenSelected;
                }));
            });
            this.showGrid = true;
            this.refreshTreeGrid();
          },
          error: (err: any) => {
            this.showGrid = true;
            this.usersService.apiCallCompleted("getPreferencesData");
          }
        });
    } else {
      this.data = [];
    }
  }
  // The tree's rows: each task with its sub-tasks as children (the old childMapping "subTasks"), from this.data.
  refreshTree() {
    const node = (task: any): any => ({ ...task, children: (task?.subTasks ?? []).map(node) });
    this.treeRows.set(this.treeUrl, LocalRowsGridApi.schema([
      ['id', 'int'], ['task', 'varchar'], [PreferencesFields.isEmailChecked, 'bit'], [PreferencesFields.isNotificationChecked, 'bit'],
      [PreferencesFields.isSMSChecked, 'bit'],
    ]), (this.data ?? []).map(node));
    this.tree?.refresh({ keepScroll: true });
  }

  refreshTreeGrid() {
    this.treeConfig ??= {
      apiBaseUrl: this.treeUrl,
      keyColumn: 'id',
      clientSide: true,
      treeColumn: 'task',
      // A tree loads only with a parameter; the rows are the page's.
      treeQueryParams: { tree: 'preferences' },
      defaultExpanded: true,
      rowHeight: 26,
      stretchColumns: true,
      enableSearch: true,
      enableFiltering: false,
      enableResetLayout: true,
      defaultColumnOrder: ['id', 'task', PreferencesFields.isEmailChecked, PreferencesFields.isNotificationChecked, PreferencesFields.isSMSChecked],
      columnLabels: { id: 'Task ID', task: 'Task Name', [PreferencesFields.isEmailChecked]: 'Email', [PreferencesFields.isNotificationChecked]: 'Notification', [PreferencesFields.isSMSChecked]: 'SMS' },
      columnWidths: { id: 50, task: 260, [PreferencesFields.isEmailChecked]: 60, [PreferencesFields.isNotificationChecked]: 60, [PreferencesFields.isSMSChecked]: 60 },
      columnAlign: {
        id: { header: { horizontal: 'left' }, horizontal: 'right' },
        task: { header: { horizontal: 'left' } },
        [PreferencesFields.isEmailChecked]: { header: { horizontal: 'left' }, horizontal: 'center' },
        [PreferencesFields.isNotificationChecked]: { header: { horizontal: 'left' }, horizontal: 'center' },
        [PreferencesFields.isSMSChecked]: { header: { horizontal: 'left' }, horizontal: 'center' },
      },
      unsortableColumns: [PreferencesFields.isEmailChecked, PreferencesFields.isNotificationChecked, PreferencesFields.isSMSChecked],
      cellTemplates: {
        [PreferencesFields.isEmailChecked]: this.emailCell,
        [PreferencesFields.isNotificationChecked]: this.notificationCell,
        [PreferencesFields.isSMSChecked]: this.smsCell,
      },
      emptyStateMessage: 'No notification preferences for this role.',
      persistenceKey: `rims.users.preferences-${this.configs?.gridId === 'sales-preferences' ? 'sales' : 'service'}`,
      persistenceVersion: 1,
    };
    this.refreshTree();
    this.usersService.apiCallCompleted("getPreferencesData");
  }

  handleActions() {
    if (this.actions != null) {
      this.actions
        .pipe(takeUntil(this.destroy$))
        .subscribe((ac: ISignal) => {
          if (ac?.action === 'reloadPermissions' && (ac?.data.category == UPEnums.UPSalesPermission || ac?.data.category == UPEnums.MPSalesPermission || ac?.data.category == UPEnums.MPServicePermission || ac?.data.category == UPEnums.UPServicePermission)) {
            this.loadPermissions(ac?.data?.category, ac?.data?.subCategory, ac?.data?.id, ac?.data?.module);
          } else if (ac?.action === 'roleChangedOrLoaded') {
            this.configs.roleId = ac?.data;
            this.getPreferencesData();
          }
          if (ac?.action === 'saveAll' && this.hasUnsavedChanges) {
            this.usersService.apiCallStarted("savePreferences");
            this.isFormSubmitted = true;
            // Every task and sub-task (the old tree's flatData): a task with sub-tasks has no parent.
            let UserPreferenceSelections: any[] = [];
            const add = (item: any) => {
              UserPreferenceSelections.push({
                id: item?.id,
                task: item?.task,
                isNotificationChecked: item?.[PreferencesFields.isNotificationChecked],
                isEmailChecked: item?.[PreferencesFields.isEmailChecked],
                isSMSChecked: item?.[PreferencesFields.isSMSChecked],
                parentId: item?.subTasks ? null : item?.parentId,
              });
              (item?.subTasks ?? []).forEach(add);
            };
            (this.data ?? []).forEach(add);
              const uniqueData = UserPreferenceSelections?.filter((item, index, self) =>
                  index === self.findIndex((t) => t.id === item.id)
              );
            let payload = {
              notificationReadExpiry: this.durationsForm.get(PreferencesFields.notificationReadExpiry)?.value,
              notificationExpiry: this.durationsForm.get(PreferencesFields.notificationExpiry)?.value,
              UserPreferenceSelections: uniqueData,
              appId: this.configs.appName,
              userId: this.configs.userId
            };
            this.usersService.savePreferences(payload).subscribe({
              next: (res: any) => {
                this.onPreferencesModified.emit(false);
                // this.commonService.showNotification('info', res.message);
                this.usersService.apiMessages.push({
                  isSuccessfull: true,
                  message:  res?.message
                });
                this.usersService.apiCallCompleted("savePreferences");
              },
              error: (err: any) => {
                // this.commonService.showNotification('error', err?.error?.message);
                        this.usersService.apiMessages.push({
                          isSuccessfull: false,
                          message:  err?.error?.message
                        });
                this.usersService.apiCallCompleted("savePreferences");
              },
            });
          }
        });
    }
  }
  private static readonly DISABLED_FIELD: Record<string, string> = {
    isEmailChecked: 'isEmailDisabled',
    isNotificationChecked: 'isNotificationDisabled',
    isSMSChecked: 'isSMSDisabled',
  };
  private static readonly COLUMN_RULE: Record<string, string> = {
    isEmailChecked: 'emailPreferencesColumn',
    isNotificationChecked: 'notificationPreferencesColumn',
    isSMSChecked: 'smsPreferencesColumn',
  };

  checkboxLocked(row: any, field: string): boolean {
    return this.getPermission(UpPreferencesComponent.COLUMN_RULE[field])?.enable == false || !this.isSectionEnabled
      || !!row?.[UpPreferencesComponent.DISABLED_FIELD[field]];
  }

  checkboxTitle(row: any, field: string): string {
    if (this.getPermission(UpPreferencesComponent.COLUMN_RULE[field])?.enable == false) return 'Your role cannot change this column';
    if (!this.isSectionEnabled) return 'This section is disabled';
    if (row?.[UpPreferencesComponent.DISABLED_FIELD[field]]) return 'Not available for this task';
    return '';
  }

  // The old changeHandler's rules, on this.data: a task with sub-tasks sets every sub-task that can change; a sub-task sets
  // itself, then its task is ticked when all its sub-tasks that can change are.
  preferencesChange(row: any, field: string, value: boolean): void {
    const disabledField = UpPreferencesComponent.DISABLED_FIELD[field];
    const parent = (this.data ?? []).find((t: any) => t?.id === row?.id);
    if (parent?.subTasks?.length) {
      parent[field] = value;
      parent.subTasks.forEach((child: any) => !child?.[disabledField] && (child[field] = value));
    } else {
      const owner = (this.data ?? []).find((t: any) => (t?.subTasks ?? []).some((c: any) => c?.id === row?.id));
      const child = owner?.subTasks?.find((c: any) => c?.id === row?.id) ?? parent;
      if (!child) return;
      child[field] = value;
      if (owner) {
        const changeable = owner.subTasks.filter((c: any) => !c?.[disabledField]);
        owner[field] = changeable.length > 0 && changeable.every((c: any) => !!c?.[field]);
      }
    }
    this.onPreferencesModified.emit(true);
    this.hasUnsavedChanges = true;
    this.refreshTree();
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.unsubscribe();
  }
}










export enum PreferencesFields {
  isEmailChecked = 'isEmailChecked',
  isNotificationChecked = 'isNotificationChecked',
  isSMSChecked = 'isSMSChecked',

  notificationExpiry = 'notificationExpiry',
  notificationExpiryDesc = 'Notification Expiration',
  notificationReadExpiry = 'notificationReadExpiry',
  notificationReadExpiryDesc = 'Read Notification Expiration',

}
